import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton, Divider, useTheme, CircularProgress, Tooltip, Paper } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { Close as CloseIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { setAnalysisUrl, setCurrentTaskId, setHistoryResult, setExtendedAnalysisResults } from 'store/reducers/actions';
import fetchPlanInfo from 'services/proAccountServices';
import { setReviewsAnalysisResults } from 'store/reducers/actions';
import { isV2Format } from 'utils/reviewsAdapter';
import ReviewsV2Container from 'sections/reviews/v2/ReviewsV2Container';
import ReviewsOutputGroup from 'sections/reviews/ReviewsOutputGroup';
import AnalysisOutputGroup from 'sections/lp-analysis/AnalysisOutputGroup';
import PictureViewer from './PictureViewer';
import MarketAnalysisReportViewer from './MarketAnalysisReportViewer';
import GetWhoToBuyReportViewer from './GetWhoToBuyReportViewer';
// Button/Chip not needed in v2-only compare flow
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import config from 'config';
import NewCompetitorsAnalysisResult from 'sections/premiumBlock/NewCompetitorsAnalysisResult';
import { addCompetitorsAnalysisResults } from 'store/reducers/actions';
import { setLpAnalysisMenuVisible, setSecondaryMenuVisible } from 'store/reducers/menu';

// Dedicated inline report pane for the Agent page. Supports LP Analysis and Reviews Analysis.
const AgentReportPane = ({ open, onClose, reportData, reportType }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const contentRef = React.useRef(null);
  const [selectionText, setSelectionText] = useState('');
  const [toolbarPos, setToolbarPos] = useState({ top: 0, left: 0, visible: false });
  const [copiedTick, setCopiedTick] = useState(false);
  const [askedTick, setAskedTick] = useState(false);

  // Precompute data and type flags with hooks at the top-level (before any return)
  const computedData = useMemo(
    () => reportData?.analysisData || reportData?.data || reportData?.result || reportData?.content || reportData?.metadata || null,
    [reportData]
  );

  const isReviews = useMemo(() => {
    const d = computedData;
    return (typeof reportType === 'string' && reportType.includes('reviews')) || (d && (d.review_analysis_results || d.rating_percentages));
  }, [computedData, reportType]);

  const isCompetitorsCompare = useMemo(() => {
    const d = computedData;
    return (
      (typeof reportType === 'string' && reportType.includes('compare_competitors')) ||
      (d && (d.url_list || d.side_by_side_analysis || d.field_by_field_analysis))
    );
  }, [computedData, reportType]);

  const isMarketAnalysis = useMemo(() => {
    const d = computedData;
    return (
      reportType === 'functions.market_analysis_tool' ||
      reportType === 'market_analysis_tool' ||
      (d && (d.market_overview || d.marketOverview || d.market_trends || d.marketTrends))
    );
  }, [computedData, reportType]);

  const isGetWhoToBuy = useMemo(() => {
    const d = computedData;
    return (
      reportType === 'functions.get_who_to_buy_strategy' ||
      reportType === 'get_who_to_buy_strategy' ||
      (d && (d.get || d.who || d.to || d.by || d.client))
    );
  }, [computedData, reportType]);

  const isPicture = useMemo(() => {
    const d = computedData;
    // Do NOT treat generic URLs as images; LP analysis commonly includes a URL field.
    // Only consider strong image signals or explicit picture report types.
    const isLandingPage =
      (typeof reportType === 'string' && reportType.includes('landing_page_analysis')) ||
      reportData?.content_type === 'lpcompare.analysistask';

    if (isLandingPage) return false;

    return (
      reportType === 'picture' ||
      reportType === 'generate_marketing_image' ||
      (d && (d.b64_image || d.image || d.image_url || (d.result && (d.result.b64_image || d.result.image_url))))
    );
  }, [computedData, reportType]);

  // Derive a readable entity title (brand/host/url) for the header
  const entityTitle = useMemo(() => {
    const data = computedData || {};
    const fromOverview = data.overview_result || data.overview || {};
    const raw =
      data.title ||
      data.name ||
      data.brand ||
      data.company ||
      data.project_name ||
      data.domain ||
      fromOverview.title ||
      fromOverview.name ||
      fromOverview.brand ||
      fromOverview.company ||
      fromOverview.project_name ||
      data.url ||
      fromOverview.url ||
      '';

    try {
      // If it's a URL, display the hostname for compactness
      if (typeof raw === 'string' && /^https?:\/\//i.test(raw)) {
        const hostname = new URL(raw).hostname.replace(/^www\./, '');
        return hostname || raw;
      }
    } catch (_) {
      // ignore
    }
    return typeof raw === 'string' ? raw : '';
  }, [computedData]);

  // Human-readable report kind (type)
  const reportKind = useMemo(() => {
    const t = String(reportType || '').toLowerCase();
    if (isGetWhoToBuy) return 'Get Who To Buy Strategy';
    if (isMarketAnalysis) return 'Market Analysis';
    if (isReviews) return 'Reviews Analysis';
    if (isCompetitorsCompare) return 'Competitors Comparison';
    if (isPicture || t.includes('picture') || t.includes('image')) return 'Image';
    if (t.includes('landing') || computedData?.content_type === 'lpcompare.analysistask') return 'Landing Page Analysis';
    return 'Analysis Report';
  }, [reportType, isGetWhoToBuy, isMarketAnalysis, isReviews, isCompetitorsCompare, isPicture, computedData]);

  const headerTitle = useMemo(() => {
    return entityTitle ? `${reportKind} — ${entityTitle}` : reportKind;
  }, [reportKind, entityTitle]);

  // Competitors v2 fetch state (declared top-level to satisfy hooks rules)
  const cmpTaskId = useMemo(() => computedData?.task_id || computedData?.competitors_search_task?.task_id || null, [computedData]);
  const [cmpLoading, setCmpLoading] = useState(false);
  const [cmpLoaded, setCmpLoaded] = useState(false);

  useEffect(() => {
    if (!isCompetitorsCompare || !open || !cmpTaskId || cmpLoaded) return;
    setCmpLoading(true);
    const auth = getAuth();
    const apiUrl = config.apiUrl;
    auth.onAuthStateChanged((user) => {
      if (!user) {
        setCmpLoading(false);
        return;
      }
      user.getIdToken().then((token) => {
        axios
          .get(`${apiUrl}/lpcompare/get_old_comp_analysis/${cmpTaskId}`, {
            params: { use_v2: true },
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
          })
          .then((response) => {
            const responseData = response.data;
            const isNew = responseData.is_new_analysis === true;
            if (isNew) {
              const dataWithFlag = { ...responseData, is_new_analysis: true };
              dispatch(addCompetitorsAnalysisResults(dataWithFlag));
              setCmpLoaded(true);
            }
          })
          .finally(() => setCmpLoading(false));
      });
    });
  }, [isCompetitorsCompare, open, cmpTaskId, cmpLoaded, dispatch]);

  useEffect(() => {
    if (!open || !reportData) return;
    // Ensure user plan/credits are loaded so AnalysisOutputGroup gates content properly
    try {
      fetchPlanInfo(dispatch);
    } catch (e) {
      // best effort
    }
    // Hide LP Analysis secondary menu and SBS secondary menu while viewing inside agent page
    dispatch(setLpAnalysisMenuVisible({ visible: false }));
    dispatch(setSecondaryMenuVisible({ visible: false }));

    // Best-effort normalization matching LP analysis shapes coming from tools
    let analysisData = computedData;
    if (!analysisData) return;

    const normalizedOverview = (function () {
      try {
        return (
          analysisData?.overview_result?.result?.[0] ||
          analysisData?.overview?.result?.[0] ||
          analysisData?.answer_data?.result?.[0] ||
          analysisData?.result?.[0] ||
          analysisData?.overview_result?.[0] ||
          analysisData?.overview?.[0] ||
          (typeof analysisData === 'object' ? analysisData : null)
        );
      } catch (e) {
        return typeof analysisData === 'object' ? analysisData : null;
      }
    })();

    const taskId =
      analysisData.task_id || analysisData?.overview_result?.task_id || analysisData?.metadata?.task_id || reportData.task_id || '';
    const url = analysisData.url || analysisData?.overview_result?.url || analysisData?.metadata?.url || reportData.url || '';

    if (taskId) dispatch(setCurrentTaskId(taskId));
    if (url) dispatch(setAnalysisUrl(url));

    // Detect reviews payload and populate reviews reducer instead
    const looksLikeReviews = isReviews;

    if (looksLikeReviews) {
      dispatch(setReviewsAnalysisResults(analysisData));
      return;
    }

    if (normalizedOverview) {
      dispatch(
        setHistoryResult({
          url,
          task_id: taskId,
          answer_data: { result: [normalizedOverview] },
          usp: analysisData.usp,
          market_overview: analysisData.market_overview,
          market_trends_and_benchmarks: analysisData.market_trends,
          segmentation: analysisData.segmentation,
          swot: analysisData.swot,
          icp: analysisData.icp,
          pricing_analysis: analysisData.pricing_analysis,
          competitors_search_task: analysisData.competitors_search_task?.result || analysisData.competitors_search_task
        })
      );

      dispatch(
        setExtendedAnalysisResults({
          usp: analysisData.usp,
          market_overview: analysisData.market_overview,
          market_trends_and_benchmarks: analysisData.market_trends,
          segmentation: analysisData.segmentation,
          swot: analysisData.swot,
          icp: analysisData.icp,
          pricing_analysis: analysisData.pricing_analysis,
          competitors_search: analysisData.competitors_search
        })
      );
    }
  }, [open, reportData, dispatch]);

  // On unmount, ensure we don't accidentally leave the menu visible state altered
  useEffect(() => {
    return () => {
      dispatch(setLpAnalysisMenuVisible({ visible: false }));
      dispatch(setSecondaryMenuVisible({ visible: false }));
    };
  }, [dispatch]);

  // Selection handling to show inline mini-toolbar (Ask in Chat / Copy)
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const updateToolbarFromSelection = () => {
      try {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || !sel.rangeCount) {
          setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));
          setSelectionText('');
          return;
        }
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        if (!rect || (rect.width === 0 && rect.height === 0)) {
          setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));
          setSelectionText('');
          return;
        }
        const containerRect = el.getBoundingClientRect();
        if (rect.bottom < containerRect.top || rect.top > containerRect.bottom) {
          setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));
          setSelectionText('');
          return;
        }
        const text = String(sel.toString() || '').trim();
        if (!text) {
          setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));
          setSelectionText('');
          return;
        }
        const scrollTop = el.scrollTop || 0;
        const scrollLeft = el.scrollLeft || 0;
        const top = scrollTop + (rect.top - containerRect.top) - 36;
        const left = scrollLeft + (rect.right - containerRect.left); // anchor to right edge
        setSelectionText(text);
        setToolbarPos({ top: Math.max(0, top), left: Math.max(0, left), visible: true });
      } catch (_) {
        // ignore
      }
    };

    const hideToolbar = () => setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));

    el.addEventListener('mouseup', updateToolbarFromSelection);
    el.addEventListener('keyup', updateToolbarFromSelection);
    el.addEventListener('scroll', hideToolbar, { passive: true });

    return () => {
      el.removeEventListener('mouseup', updateToolbarFromSelection);
      el.removeEventListener('keyup', updateToolbarFromSelection);
      el.removeEventListener('scroll', hideToolbar);
    };
  }, [contentRef]);

  const handleCopySelection = async () => {
    try {
      if (!selectionText) return;
      await navigator.clipboard.writeText(selectionText);
      setCopiedTick(true);
      setTimeout(() => setCopiedTick(false), 1000);
    } catch (_) {
      // ignore
    }
  };

  const handleAskInChat = () => {
    if (!selectionText) return;
    try {
      window.dispatchEvent(new CustomEvent('chat:insert_text', { detail: { text: selectionText } }));
      setAskedTick(true);
      setTimeout(() => setAskedTick(false), 1000);
    } catch (_) {
      // ignore
    }
  };

  if (!open || !reportData)
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">No report selected yet</Typography>
      </Box>
    );

  if (isGetWhoToBuy) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
            {headerTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', backgroundColor: theme.palette.background.default, position: 'relative' }}>
          <GetWhoToBuyReportViewer reportData={reportData} />
          {toolbarPos.visible && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: toolbarPos.top,
                left: toolbarPos.left,
                transform: 'translateX(-100%)',
                p: 0.5,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5
              }}
            >
              <Tooltip title={askedTick ? 'Added to chat' : 'Ask in chat'}>
                <span>
                  <IconButton size="small" onClick={handleAskInChat} sx={{ width: 28, height: 28 }}>
                    <QuestionAnswerIcon fontSize="small" color={askedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={copiedTick ? 'Copied' : 'Copy'}>
                <span>
                  <IconButton size="small" onClick={handleCopySelection} sx={{ width: 28, height: 28 }}>
                    <ContentCopyIcon fontSize="small" color={copiedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          )}
        </Box>
      </Box>
    );
  }

  if (isMarketAnalysis) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
            {headerTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', backgroundColor: theme.palette.background.default, position: 'relative' }}>
          <MarketAnalysisReportViewer reportData={reportData} />
          {toolbarPos.visible && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: toolbarPos.top,
                left: toolbarPos.left,
                transform: 'translateX(-100%)',
                p: 0.5,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5
              }}
            >
              <Tooltip title={askedTick ? 'Added to chat' : 'Ask in chat'}>
                <span>
                  <IconButton size="small" onClick={handleAskInChat} sx={{ width: 28, height: 28 }}>
                    <QuestionAnswerIcon fontSize="small" color={askedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={copiedTick ? 'Copied' : 'Copy'}>
                <span>
                  <IconButton size="small" onClick={handleCopySelection} sx={{ width: 28, height: 28 }}>
                    <ContentCopyIcon fontSize="small" color={copiedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          )}
        </Box>
      </Box>
    );
  }

  if (isReviews) {
    const v2 = isV2Format(computedData || {});
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
            {headerTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', backgroundColor: theme.palette.background.default, position: 'relative' }}>
          {v2 ? <ReviewsV2Container /> : <ReviewsOutputGroup />}
          {toolbarPos.visible && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: toolbarPos.top,
                left: toolbarPos.left,
                transform: 'translateX(-100%)',
                p: 0.5,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5
              }}
            >
              <Tooltip title={askedTick ? 'Added to chat' : 'Ask in chat'}>
                <span>
                  <IconButton size="small" onClick={handleAskInChat} sx={{ width: 28, height: 28 }}>
                    <QuestionAnswerIcon fontSize="small" color={askedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={copiedTick ? 'Copied' : 'Copy'}>
                <span>
                  <IconButton size="small" onClick={handleCopySelection} sx={{ width: 28, height: 28 }}>
                    <ContentCopyIcon fontSize="small" color={copiedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          )}
        </Box>
      </Box>
    );
  }

  if (isCompetitorsCompare) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
            {headerTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', backgroundColor: theme.palette.background.default, position: 'relative' }}>
          {cmpLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                Loading comparison…
              </Typography>
            </Box>
          )}
          {!cmpLoading && cmpLoaded && (
            <Box
              sx={{
                // Ensure inner containers stretch fully in the agent pane
                '& .MuiContainer-root': {
                  maxWidth: '100% !important',
                  paddingLeft: { xs: 2, md: 3 },
                  paddingRight: { xs: 2, md: 3 },
                  marginLeft: '0 !important',
                  marginRight: '0 !important'
                },
                '& .MuiContainer-maxWidthLg, & .MuiContainer-maxWidthMd, & .MuiContainer-maxWidthXl': {
                  maxWidth: '100% !important'
                },
                // Neutralize the global main-content left margin added by secondary menu logic
                '& [data-agent-pane-reset-margin="true"]': {
                  ml: '0 !important'
                }
              }}
            >
              <Box data-agent-pane-reset-margin="true">
                <NewCompetitorsAnalysisResult showSecondaryMenu={false} />
              </Box>
            </Box>
          )}
          {!cmpLoading && !cmpLoaded && (
            <Typography variant="body2" color="text.secondary">
              Comparison not available in v2 format.
            </Typography>
          )}
          {toolbarPos.visible && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: toolbarPos.top,
                left: toolbarPos.left,
                transform: 'translateX(-100%)',
                p: 0.5,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5
              }}
            >
              <Tooltip title={askedTick ? 'Added to chat' : 'Ask in chat'}>
                <span>
                  <IconButton size="small" onClick={handleAskInChat} sx={{ width: 28, height: 28 }}>
                    <QuestionAnswerIcon fontSize="small" color={askedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={copiedTick ? 'Copied' : 'Copy'}>
                <span>
                  <IconButton size="small" onClick={handleCopySelection} sx={{ width: 28, height: 28 }}>
                    <ContentCopyIcon fontSize="small" color={copiedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          )}
        </Box>
      </Box>
    );
  }

  if (isPicture) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
            {headerTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', backgroundColor: theme.palette.background.default, position: 'relative' }}>
          <PictureViewer data={reportData} />
          {toolbarPos.visible && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: toolbarPos.top,
                left: toolbarPos.left,
                transform: 'translateX(-100%)',
                p: 0.5,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5
              }}
            >
              <Tooltip title={askedTick ? 'Added to chat' : 'Ask in chat'}>
                <span>
                  <IconButton size="small" onClick={handleAskInChat} sx={{ width: 28, height: 28 }}>
                    <QuestionAnswerIcon fontSize="small" color={askedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={copiedTick ? 'Copied' : 'Copy'}>
                <span>
                  <IconButton size="small" onClick={handleCopySelection} sx={{ width: 28, height: 28 }}>
                    <ContentCopyIcon fontSize="small" color={copiedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
          {headerTitle}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', backgroundColor: theme.palette.background.default, position: 'relative' }}>
        <AnalysisOutputGroup />
        {toolbarPos.visible && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: toolbarPos.top,
              left: toolbarPos.left,
              transform: 'translateX(-100%)',
              p: 0.5,
              borderRadius: 2,
              display: 'flex',
              gap: 0.5
            }}
          >
            <Tooltip title={askedTick ? 'Added to chat' : 'Ask in chat'}>
              <span>
                <IconButton size="small" onClick={handleAskInChat} sx={{ width: 28, height: 28 }}>
                  <QuestionAnswerIcon fontSize="small" color={askedTick ? 'success' : 'action'} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={copiedTick ? 'Copied' : 'Copy'}>
              <span>
                <IconButton size="small" onClick={handleCopySelection} sx={{ width: 28, height: 28 }}>
                  <ContentCopyIcon fontSize="small" color={copiedTick ? 'success' : 'action'} />
                </IconButton>
              </span>
            </Tooltip>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default AgentReportPane;
